import { useState, useEffect } from 'react'
import styles from './ShareProjectModal.module.scss'
import { withLocalize } from 'react-localize-redux'
import { Typography, Button, InputAdornment, TextField } from '@material-ui/core'
import SearchOutlinedIcon from '@material-ui/icons/SearchOutlined'
import ButtonComponent from 'common/components/Button/Button'
import cx from 'classnames'
import { useForm } from 'react-hook-form'
import PropTypes from 'prop-types'
import { getUser } from 'common/selectors/user'
import { connect } from 'react-redux'
import { showModal } from 'common/actions/modal'
import { LoadingButton } from '@mui/lab'
import InfoIcon from 'common/components/InfoIcon/InfoIcon'
import SquareIconButton from 'common/components/SquareIconButton/SquareIconButton'
import { Tooltip } from '@mui/material'
import * as ModalTypes from 'constants/ModalTypes'
import { ReactComponent as PlusSquareIcon } from 'icons/PlusSquare.svg'
import { ReactComponent as XSquareIcon } from 'icons/XSquare.svg'
import { ReactComponent as WarningTriangleIcon } from 'icons/WarningTriangle.svg'

// Roles válidos para compartir proyecto
const ALLOWED_ROLES = ['viewer', 'manager']

// The design puts an icon and a link inside a sentence, so the translated
// string carries a {token} the component replaces with a node. Same idea as
// rssiActive's {value}, except the replacement is an element and not a string.
const splitAround = (text, token) => {
  const at = text.indexOf(token)
  return at === -1 ? [text, ''] : [text.slice(0, at), text.slice(at + token.length)]
}

// Addresses are matched case-insensitively: legacy data holds the same person
// spelled differently in the store and in the project, and keying state by the
// raw spelling used to show them as two different people.
const keyOf = email => (email || '').toLowerCase()

const ShareProjectModal = props => {
  const { translate, isSubmitting, user, project, onSubmit, showModal } = props
  const [trustedUser, setTrustedUser] = useState([])
  const [emails, setEmails] = useState({})
  const [emailsRole, setEmailsRole] = useState({})
  const [search, setSearch] = useState('')
  const { errors, setError, clearErrors } = useForm()
  const currentUserEmail = user?.toJS?.().email || ''
  const currentUserKey = keyOf(currentUserEmail)

  const onClickCancel = () => {
    props.hideModal()
  }

  // The modal root renders one modal at a time, so showing Manage Users
  // replaces this one. Anything picked here is dropped with it.
  const handleOpenManageUsers = () => {
    showModal({
      type: ModalTypes.MANAGE_TRUSTED_USERS,
      props: { email: currentUserEmail }
    })
  }

  useEffect(() => {
    if (project && project.Meta?.ProjectCustomers) {
      const initialEmails = {}
      const initialEmailsRole = {}
      Object.values(project.Meta.ProjectCustomers).forEach(customer => {
        if (customer.email && keyOf(customer.email) !== currentUserKey) {
          initialEmails[keyOf(customer.email)] = true
          initialEmailsRole[keyOf(customer.email)] = customer.role || 'assignRole'
        }
      })
      setEmails(initialEmails)
      setEmailsRole(initialEmailsRole)
    }
  }, [project, currentUserKey])

  useEffect(() => {
    const { trustedUser: t = [], unTrustedUser: u = [] } = user.toJS()

    // The store can hold the same address several times, and in both lists at
    // once, because MANAGE_TRUSTED_USERS_REQUEST.SUCCESS concatenates on every
    // save. One row per address here: the assigned flag is keyed by address,
    // so two rows for one person could never hold different state anyway.
    const byAddress = new Map()

    ;[
      ...t.map(email => ({ email, confirmed: true })),
      ...u.map(email => ({ email, confirmed: false }))
    ]
      .filter(({ email }) => keyOf(email) !== currentUserKey)
      .forEach(entry => {
        const key = keyOf(entry.email)
        const seen = byAddress.get(key)
        // Already accepted beats a stale pending copy of the same address
        if (!seen || (entry.confirmed && !seen.confirmed)) {
          byAddress.set(key, entry)
        }
      })

    setTrustedUser([...byAddress.values()])
  }, [user, currentUserKey])

  const handleAssignUser = email => {
    setEmails({ ...emails, [keyOf(email)]: true })
  }

  const handleRemoveUser = email => {
    clearErrors('assignRole')
    clearErrors('userSelfAssign')
    setEmails({ ...emails, [keyOf(email)]: false })
  }

  const handlechangeRole = (event, email) => {
    clearErrors('assignRole')
    setEmailsRole({
      ...emailsRole,
      [keyOf(email)]: event.target.value
    })
  }

  const handleShareUserProject = (emailsMap, roles) => {
    // Limpiar errores previos
    clearErrors('userSelfAssign')
    clearErrors('assignRole')

    // Emails asignados
    const checkedEmails = Object.keys(emailsMap || {}).filter(email => emailsMap[email])

    // 1) Si no hay asignados -> permitir guardar (lista vacía)
    if (checkedEmails.length === 0) {
      onSubmit({ attachedUsers: {} })
      return
    }

    // 2) Si hay asignados, validar que ninguno es el propio usuario
    if (checkedEmails.some(key => key === currentUserKey)) {
      setError('userSelfAssign', { shouldFocus: 'assignRole' })
      return
    }

    // 3) Validar que TODOS tienen un rol permitido (no 'assignRole')
    const missingRole = checkedEmails.filter(key => !ALLOWED_ROLES.includes(roles?.[key]))
    if (missingRole.length > 0) {
      setError('assignRole', { shouldFocus: 'assignRole' })
      return
    }

    // Keys are already lowercased, which is what shareProject stores: it
    // lowercases every address on arrival, so the spelling sent never matters
    const attachedUsers = checkedEmails.reduce((acc, key) => {
      acc[key] = roles[key]
      return acc
    }, {})
    onSubmit({ attachedUsers })
  }

  const assignedUsers = trustedUser.filter(({ email }) => emails[keyOf(email)])
  const unassignedUsers = trustedUser.filter(({ email }) => !emails[keyOf(email)])
  const query = search.trim().toLowerCase()
  const visibleUnassignedUsers = query
    ? unassignedUsers.filter(({ email }) => keyOf(email).includes(query))
    : unassignedUsers

  const [manageUsersHintBefore, manageUsersHintAfter] = splitAround(
    translate('addNewUsersHint'),
    '{link}'
  )
  const [assignUserHelpBefore, assignUserHelpAfter] = splitAround(
    translate('assignUserHelp'),
    '{icon}'
  )
  const [assignRoleHelpBefore, assignRoleHelpAfter] = splitAround(
    translate('assignRoleOrRemoveHelp'),
    '{icon}'
  )

  const renderEmail = ({ email, confirmed }) => (
    <Tooltip
      key={email + 'tooltip'}
      title={email}
      enterDelay={1500}
      leaveDelay={100}
    >
      <Typography
        className={cx(styles.userEmail, confirmed ? styles.confirmed : styles.notConfirmed)}
      >
        {email}
      </Typography>
    </Tooltip>
  )

  return (
    <div className={styles.modalContainer}>
      <Typography className={styles.header}>{translate('shareProjectTitle')}</Typography>
      <div className={styles.modal}>
        <form
          noValidate
          onSubmit={event => event.preventDefault()}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault() // Prevent form submission on Enter key press
            }
          }}
        >
          <div>
            <Typography className={styles.shareProject}>{translate('shareProject')}</Typography>

            <TextField
              className={styles.searchBar}
              placeholder={translate('searchUser')}
              onChange={event => setSearch(event.target.value)}
              value={search}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchOutlinedIcon />
                  </InputAdornment>
                )
              }}
              variant='outlined'
            />

            <Typography className={styles.manageUsersHint}>
              <WarningTriangleIcon
                aria-hidden='true'
                focusable='false'
                className={styles.hintIcon}
              />
              {/* One flex item, so the link wraps with the sentence instead of
                  becoming its own column when the text needs two lines */}
              <span>
                {manageUsersHintBefore}
                <button
                  type='button'
                  className={styles.manageUsersLink}
                  onClick={handleOpenManageUsers}
                >
                  {translate('manageUsers')}
                </button>
                {manageUsersHintAfter}
              </span>
            </Typography>

            <Typography className={styles.sectionTitle}>{translate('assignUserTitle')}</Typography>
            <Typography className={styles.sectionHelp}>
              {assignUserHelpBefore}
              <PlusSquareIcon
                aria-hidden='true'
                focusable='false'
                className={styles.inlineIcon}
              />
              {assignUserHelpAfter}
            </Typography>

            <div className={styles.userList}>
              {visibleUnassignedUsers.map(userEntry => (
                <div
                  key={userEntry.email + 'unassigned'}
                  className={styles.userRow}
                >
                  <SquareIconButton
                    ariaLabel={`${translate('assignUser')}: ${userEntry.email}`}
                    Icon={PlusSquareIcon}
                    onClick={() => handleAssignUser(userEntry.email)}
                    variant='add'
                  />
                  {renderEmail(userEntry)}
                </div>
              ))}
              {visibleUnassignedUsers.length === 0 && (
                <Typography className={styles.emptyList}>
                  {trustedUser.length === 0
                    ? translate('noUsersYet')
                    : unassignedUsers.length === 0
                    ? translate('allUsersAssigned')
                    : translate('noUsersFound')}
                </Typography>
              )}
            </div>

            <div className={styles.existingRoleContainer}>
              <Typography className={styles.sectionTitle}>
                {translate('assignRoleOrRemoveTitle')}
              </Typography>
              <InfoIcon
                content={
                  <>
                    <Typography className={styles.subTitle}>
                      {translate('rolesDescription')}
                    </Typography>
                    <Typography className={styles.infoText}>
                      <b>{translate('viewer')}</b>
                      {': '}
                      {translate('viewerInfos')}
                    </Typography>
                    <Typography className={styles.infoText}>
                      <b>{translate('manager')}</b>
                      {': '}
                      {translate('managerInfo')}
                    </Typography>
                    <ButtonComponent
                      label={translate('rolesKnowMore')}
                      onClick={() =>
                        window.open('https://www.inbiotair.com/repository/rol-management')
                      }
                      type='next'
                      className={styles.knowMore}
                    />
                  </>
                }
                icon={
                  <img
                    src='../images/info.svg'
                    className={cx('img_info', styles.infoIcon)}
                    alt='info'
                  />
                }
              />
            </div>
            <Typography className={styles.sectionHelp}>
              {assignRoleHelpBefore}
              <XSquareIcon
                aria-hidden='true'
                focusable='false'
                className={cx(styles.inlineIcon, styles.inlineIconRemove)}
              />
              {assignRoleHelpAfter}
            </Typography>

            <div className={styles.userList}>
              {assignedUsers.map(userEntry => (
                <div
                  key={userEntry.email + 'assigned'}
                  className={styles.userRow}
                >
                  <SquareIconButton
                    ariaLabel={`${translate('removeUser')}: ${userEntry.email}`}
                    Icon={XSquareIcon}
                    onClick={() => handleRemoveUser(userEntry.email)}
                    variant='remove'
                  />
                  {renderEmail(userEntry)}
                  <select
                    className={styles.selectRange}
                    value={emailsRole[keyOf(userEntry.email)] || 'assignRole'}
                    onChange={event => handlechangeRole(event, userEntry.email)}
                  >
                    <option
                      value='assignRole'
                      disabled
                      className={styles.optionStyle}
                    >
                      {translate('assignRole')}
                    </option>

                    <option value='viewer'>{translate('viewer')}</option>
                    <option value='manager'>{translate('manager')}</option>
                  </select>
                </div>
              ))}
              {assignedUsers.length === 0 && (
                <Typography className={styles.emptyList}>
                  {translate('noUsersAssignedYet')}
                </Typography>
              )}
            </div>

            {errors.assignRole && (
              <div className={styles.error}>
                <small>{translate('validation.assignRole')}</small>
              </div>
            )}
            {errors.userSelfAssign && (
              <div className={styles.error}>
                <small>{translate('validation.userSelfAssign')}</small>
              </div>
            )}

            <div className={styles.cselect}>
              <div className={styles.buttonWrapper}>
                <LoadingButton
                  fullWidth
                  variant='contained'
                  className={styles.buttonLoadingSave}
                  disabled={isSubmitting}
                  loading={isSubmitting}
                  onClick={() => handleShareUserProject(emails, emailsRole)}
                >
                  {translate('save')}
                </LoadingButton>
              </div>
              <div className={styles.buttonWrapper}>
                <Button
                  fullWidth
                  variant='contained'
                  className={styles.buttonCancelar}
                  disabled={isSubmitting}
                  onClick={onClickCancel}
                >
                  {translate('cancel')}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

ShareProjectModal.propTypes = {
  isSubmitting: PropTypes.bool.isRequired,
  translate: PropTypes.func.isRequired
}

const mapStateToProps = state => ({
  user: getUser(state)
})
const mapActionsToProps = {
  showModal
}
export default withLocalize(connect(mapStateToProps, mapActionsToProps)(ShareProjectModal))
